import React from 'react';
import { MapStats } from '../../../models/MapStats';
import styles from './MapSectionsView.module.css';

import inferno from '../../../content/maps/de_inferno.jpg';
import mirage from '../../../content/maps/de_mirage.jpg';
import nuke from '../../../content/maps/de_nuke.jpg';
import overpass from '../../../content/maps/de_overpass.jpg';
import ancient from '../../../content/maps/de_ancient.jpg';
import vertigo from '../../../content/maps/de_vertigo.jpg';
import anubis from '../../../content/maps/de_anubis.jpg';
import dust2 from '../../../content/maps/de_dust2.jpg';
import train from '../../../content/maps/de_train.jpg';
import cache from '../../../content/maps/de_cache.jpg';

const mapImages: Record<string, string> = {
    Inferno: inferno, Mirage: mirage, Nuke: nuke, Overpass: overpass,
    Ancient: ancient, Vertigo: vertigo, Anubis: anubis, Dust2: dust2, Train: train,
    Cache: cache,
};

const getWinRateColor = (rate: number): string => {
    if (rate >= 65) return '#4ade80';
    if (rate >= 50) return '#e2e8f0';
    if (rate > 0) return '#fbbf24';
    return '#f87171';
};

interface Props {
    mapStats: MapStats[];
}

const MapSectionsView: React.FC<Props> = ({ mapStats }) => {
    if (mapStats.length === 0) {
        return <div className={styles.noData}>No map data available</div>;
    }

    const sorted = [...mapStats].sort((a, b) => {
        const wr = (s: MapStats) => s.amountPlayed > 0 ? s.amountWon / s.amountPlayed : 0;
        return wr(b) - wr(a);
    });

    return (
        <div className={styles.container}>
            {sorted.map((stat, index) => {
                const winRate = stat.amountPlayed > 0
                    ? Math.round((stat.amountWon / stat.amountPlayed) * 100)
                    : 0;
                const losses = stat.amountPlayed - stat.amountWon;
                const color = getWinRateColor(winRate);
                const tPct = stat.chosenCTPercentage > 0 ? Math.round(100 - stat.chosenCTPercentage) : 0;

                return (
                    <div
                        key={stat.mapName}
                        className={`${styles.section} ${index % 2 === 0 ? styles.sectionEven : styles.sectionOdd}`}
                    >
                        {/* Map image */}
                        <div className={styles.mapArea}>
                            <img src={mapImages[stat.mapName]} alt={stat.mapName} className={styles.mapImg} />
                            <div className={styles.mapOverlay}>
                                <span className={styles.mapLabel}>{stat.mapName}</span>
                            </div>
                        </div>

                        {/* Stats grid */}
                        <div className={styles.statsGrid}>
                            {/* Win Rate */}
                            <div className={styles.statBlock}>
                                <span className={styles.statLabel}>Win Rate</span>
                                <span className={styles.winRateValue} style={{ color }}>
                                    {stat.amountPlayed > 0 ? `${winRate}%` : '—'}
                                </span>
                                <div className={styles.winRateBar}>
                                    <div
                                        className={styles.winRateBarFill}
                                        style={{ width: `${winRate}%`, backgroundColor: color }}
                                    />
                                </div>
                            </div>

                            {/* Record */}
                            <div className={styles.statBlock}>
                                <span className={styles.statLabel}>Record</span>
                                <div className={styles.recordValue}>
                                    <span className={`${styles.recordNum} ${styles.winColor}`}>{stat.amountWon}</span>
                                    <span className={styles.recordLabel + ' ' + styles.winColor}>W</span>
                                    <span className={styles.recordSep}>/</span>
                                    <span className={`${styles.recordNum} ${styles.lossColor}`}>{losses}</span>
                                    <span className={styles.recordLabel + ' ' + styles.lossColor}>L</span>
                                </div>
                            </div>

                            {/* Pick / Ban */}
                            <div className={styles.statBlock}>
                                <span className={styles.statLabel}>Pick / Ban</span>
                                <div className={styles.pickBanRow}>
                                    <span className={`${styles.pickBanBadge} ${styles.pickColor}`}>
                                        {stat.amountPicked} pick
                                    </span>
                                    <span className={`${styles.pickBanBadge} ${styles.banColor}`}>
                                        {stat.amountBanned} ban
                                    </span>
                                </div>
                            </div>

                            {/* CT Choice */}
                            <div className={styles.statBlock}>
                                <span className={styles.statLabel}>Side Choice</span>
                                {stat.chosenCTPercentage > 0 ? (
                                    <div className={styles.ctRow}>
                                        <span className={`${styles.ctTag} ${styles.ctTagBlue}`}>
                                            CT {stat.chosenCTPercentage}%
                                        </span>
                                        <span className={`${styles.ctTag} ${styles.ctTagYellow}`}>
                                            T {tPct}%
                                        </span>
                                    </div>
                                ) : (
                                    <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>—</span>
                                )}
                            </div>
                        </div>

                        {/* Played count */}
                        <div className={styles.playedCount}>
                            <div className={styles.playedNum}>{stat.amountPlayed}</div>
                            <div className={styles.playedLabel}>Maps</div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default MapSectionsView;
